import React, { useState } from 'react';
import { PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Calendar, Target, Settings, Download } from 'lucide-react';
import StravaAuth from './StravaAuth';
import AuthCallback from './AuthCallback';
import type { AuthState } from '../utils/auth/types';

// 型定義
interface PowerZones {
  zone1: number;
  zone2: number;
  zone3: number;
  zone4: number;
  zone5: number;
  zone6: number;
  [key: string]: number;
}

interface WeeklyData {
  week: string;
  actualZones: PowerZones;
  actualTSS: number;
  rides: number;
}

const PowerZoneDashboard: React.FC = () => {
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [targetZones, setTargetZones] = useState<PowerZones>({
    zone1: 10, zone2: 20, zone3: 25, zone4: 25, zone5: 15, zone6: 5
  });
  const [weeklyTSSTarget, setWeeklyTSSTarget] = useState(350);
  const [showSettings, setShowSettings] = useState(false);
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
    athlete: null,
    error: null
  });

  // 認証コールバックかどうかをチェック
  const isAuthCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
  };

  // 認証状態が変更された時のハンドラー
  const handleAuthStateChange = (newAuthState: AuthState) => {
    setAuthState(newAuthState);
  };

  // パワーゾーンの色設定（Zwift準拠）
  const zoneColors: {[key: string]: string} = {
    zone1: '#6b7280', // Recovery - Grey
    zone2: '#3b82f6', // Endurance - Blue
    zone3: '#10b981', // Tempo - Green
    zone4: '#f59e0b', // Threshold - Yellow
    zone5: '#f97316', // VO2 Max - Orange
    zone6: '#ef4444'  // Anaerobic - Red
  };

  // サンプルデータ（実際のStravaAPIからのデータに置き換え）
  const weeklyData: WeeklyData[] = [
    {
      week: '2025/07/14-20',
      actualZones: { zone1: 15, zone2: 25, zone3: 30, zone4: 20, zone5: 8, zone6: 2 },
      actualTSS: 320,
      rides: 4
    },
    {
      week: '2025/07/07-13',
      actualZones: { zone1: 10, zone2: 30, zone3: 35, zone4: 20, zone5: 4, zone6: 1 },
      actualTSS: 380,
      rides: 5
    },
    {
      week: '2025/06/30-06',
      actualZones: { zone1: 8, zone2: 22, zone3: 30, zone4: 30, zone5: 8, zone6: 2 },
      actualTSS: 420,
      rides: 6
    },
    {
      week: '2025/06/23-29',
      actualZones: { zone1: 20, zone2: 35, zone3: 25, zone4: 15, zone5: 4, zone6: 1 },
      actualTSS: 290,
      rides: 3
    }
  ];


  // パイチャート用データ
  const getPieData = () => {
    const current = weeklyData[selectedWeek];
    return [
      { name: 'Zone 1 (Recovery)', value: current.actualZones.zone1, fill: zoneColors.zone1 },
      { name: 'Zone 2 (Endurance)', value: current.actualZones.zone2, fill: zoneColors.zone2 },
      { name: 'Zone 3 (Tempo)', value: current.actualZones.zone3, fill: zoneColors.zone3 },
      { name: 'Zone 4 (Threshold)', value: current.actualZones.zone4, fill: zoneColors.zone4 },
      { name: 'Zone 5 (VO2 Max)', value: current.actualZones.zone5, fill: zoneColors.zone5 },
      { name: 'Zone 6 (Anaerobic)', value: current.actualZones.zone6, fill: zoneColors.zone6 }
    ];
  };

  // TSS推移データ（新しい日付を右側に）
  const getTSSProgressData = () => {
    return weeklyData.slice().reverse().map((week) => ({
      week: week.week.split('/')[1] + '/' + week.week.split('/')[2].split('-')[0],
      actual: week.actualTSS,
      target: weeklyTSSTarget,
      achievement: Math.round((week.actualTSS / weeklyTSSTarget) * 100)
    }));
  };

  const currentWeek = weeklyData[selectedWeek];
  const tssAchievement = Math.round((currentWeek.actualTSS / weeklyTSSTarget) * 100);

  // 認証コールバックページの表示
  if (isAuthCallback()) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Power Zone Dashboard
            </h1>
            <p className="text-slate-300 mt-2">Stravaデータに基づくパワーゾーン分析</p>
          </div>
          <div className="flex gap-3 items-center">
            {/* Strava認証コンポーネント */}
            <StravaAuth onAuthStateChange={handleAuthStateChange} />
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Settings size={18} />
              設定
            </button>
            <button className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
              <Download size={18} />
              エクスポート
            </button>
          </div>
        </div>

        {/* 設定パネル */}
        {showSettings && (
          <div className="bg-slate-800 rounded-xl p-6 mb-6 border border-slate-700">
            <h3 className="text-xl font-semibold mb-4">目標設定</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3">パワーゾーン目標割合 (%)</h4>
                <div className="space-y-3">
                  {Object.entries(targetZones).map(([zone, value]) => (
                    <div key={zone} className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: zoneColors[zone] }}
                      ></div>
                      <label className="text-sm w-16">{zone.replace('zone', 'Zone ')}</label>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        value={value}
                        onChange={(e) => setTargetZones({...targetZones, [zone]: parseInt(e.target.value)})}
                        className="flex-1"
                      />
                      <span className="text-sm w-8">{value}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3">週間TSS目標</h4>
                <input
                  type="number"
                  value={weeklyTSSTarget}
                  onChange={(e) => setWeeklyTSSTarget(parseInt(e.target.value))}
                  className="bg-slate-700 border border-slate-600 rounded px-3 py-2 w-full text-white"
                  placeholder="週間TSS目標"
                />
              </div>
            </div>
          </div>
        )}

        {/* 週選択 */}
        <div className="bg-slate-800 rounded-xl p-4 mb-6 border border-slate-700">
          <div className="flex items-center gap-4">
            <Calendar className="text-blue-400" size={20} />
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
              className="bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white"
            >
              {weeklyData.map((week, index) => (
                <option key={index} value={index}>{week.week}</option>
              ))}
            </select>
            <div className="ml-auto text-sm text-slate-300">
              ライド数: {currentWeek.rides} 回
            </div>
          </div>
        </div>

        {/* メトリクス概要 */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 max-w-md mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <Target className="text-green-400" size={24} />
              <h3 className="text-lg font-semibold">TSS達成率</h3>
            </div>
            <div className="text-3xl font-bold text-green-400 mb-2">{tssAchievement}%</div>
            <div className="text-sm text-slate-300">
              {currentWeek.actualTSS} / {weeklyTSSTarget} TSS
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2 mt-3">
              <div 
                className="bg-green-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(tssAchievement, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* チャート */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* パワーゾーン比較 */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h3 className="text-xl font-semibold mb-4">パワーゾーン比較</h3>
            <div className="space-y-6">
              {/* 実際の分布 */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-300">実際</span>
                  <span className="text-xs text-slate-400">100%</span>
                </div>
                <div className="flex h-12 rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone1, 
                      width: `${weeklyData[selectedWeek].actualZones.zone1}%` 
                    }}
                  >
                    {weeklyData[selectedWeek].actualZones.zone1 > 8 ? `${weeklyData[selectedWeek].actualZones.zone1}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone2, 
                      width: `${weeklyData[selectedWeek].actualZones.zone2}%` 
                    }}
                  >
                    {weeklyData[selectedWeek].actualZones.zone2 > 8 ? `${weeklyData[selectedWeek].actualZones.zone2}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone3, 
                      width: `${weeklyData[selectedWeek].actualZones.zone3}%` 
                    }}
                  >
                    {weeklyData[selectedWeek].actualZones.zone3 > 8 ? `${weeklyData[selectedWeek].actualZones.zone3}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone4, 
                      width: `${weeklyData[selectedWeek].actualZones.zone4}%` 
                    }}
                  >
                    {weeklyData[selectedWeek].actualZones.zone4 > 8 ? `${weeklyData[selectedWeek].actualZones.zone4}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone5, 
                      width: `${weeklyData[selectedWeek].actualZones.zone5}%` 
                    }}
                  >
                    {weeklyData[selectedWeek].actualZones.zone5 > 8 ? `${weeklyData[selectedWeek].actualZones.zone5}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone6, 
                      width: `${weeklyData[selectedWeek].actualZones.zone6}%` 
                    }}
                  >
                    {weeklyData[selectedWeek].actualZones.zone6 > 8 ? `${weeklyData[selectedWeek].actualZones.zone6}%` : ''}
                  </div>
                </div>
              </div>

              {/* 目標の分布 */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-300">目標</span>
                  <span className="text-xs text-slate-400">100%</span>
                </div>
                <div className="flex h-12 rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone1, 
                      width: `${targetZones.zone1}%` 
                    }}
                  >
                    {targetZones.zone1 > 8 ? `${targetZones.zone1}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone2, 
                      width: `${targetZones.zone2}%` 
                    }}
                  >
                    {targetZones.zone2 > 8 ? `${targetZones.zone2}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone3, 
                      width: `${targetZones.zone3}%` 
                    }}
                  >
                    {targetZones.zone3 > 8 ? `${targetZones.zone3}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone4, 
                      width: `${targetZones.zone4}%` 
                    }}
                  >
                    {targetZones.zone4 > 8 ? `${targetZones.zone4}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone5, 
                      width: `${targetZones.zone5}%` 
                    }}
                  >
                    {targetZones.zone5 > 8 ? `${targetZones.zone5}%` : ''}
                  </div>
                  <div 
                    className="flex items-center justify-center text-white text-sm font-medium"
                    style={{ 
                      backgroundColor: zoneColors.zone6, 
                      width: `${targetZones.zone6}%` 
                    }}
                  >
                    {targetZones.zone6 > 8 ? `${targetZones.zone6}%` : ''}
                  </div>
                </div>
              </div>

              {/* 凡例 */}
              <div className="flex flex-wrap gap-4 justify-center pt-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: zoneColors.zone1 }}></div>
                  <span className="text-xs text-slate-300">Zone 1 (Recovery)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: zoneColors.zone2 }}></div>
                  <span className="text-xs text-slate-300">Zone 2 (Endurance)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: zoneColors.zone3 }}></div>
                  <span className="text-xs text-slate-300">Zone 3 (Tempo)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: zoneColors.zone4 }}></div>
                  <span className="text-xs text-slate-300">Zone 4 (Threshold)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: zoneColors.zone5 }}></div>
                  <span className="text-xs text-slate-300">Zone 5 (VO2 Max)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: zoneColors.zone6 }}></div>
                  <span className="text-xs text-slate-300">Zone 6 (Anaerobic)</span>
                </div>
              </div>
            </div>
          </div>

          {/* パワーゾーン分布 */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h3 className="text-xl font-semibold mb-4">パワーゾーン分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={getPieData()}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={(entry) => `${entry.value}%`}
                >
                  {getPieData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TSS推移 */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-semibold mb-4">TSS推移</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={getTSSProgressData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="week" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="actual" 
                stroke="#22d3ee" 
                strokeWidth={3}
                name="実際のTSS"
                dot={{ fill: '#22d3ee', strokeWidth: 2, r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="target" 
                stroke="#6b7280" 
                strokeWidth={2}
                strokeDasharray="5 5"
                name="目標TSS"
                dot={{ fill: '#6b7280', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 認証状態に応じたメッセージ */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mt-6">
          {authState.isAuthenticated ? (
            <>
              <h3 className="text-lg font-semibold mb-3 text-green-400">Strava連携完了</h3>
              <div className="text-sm text-slate-300 space-y-2">
                <p>• ✅ Stravaアカウントと連携済み</p>
                <p>• ✅ パワーデータの取得準備完了</p>
                <p>• 🔄 現在はサンプルデータを表示中（実装予定）</p>
                <p>• 📊 パワーメーターのあるアクティビティから自動分析を実行</p>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-3">Strava API接続について</h3>
              <div className="text-sm text-slate-300 space-y-2">
                <p>• 実際のデータを取得するには、Strava APIの認証が必要です</p>
                <p>• 必要な権限: activity:read_all, profile:read_all</p>
                <p>• このデモではサンプルデータを使用しています</p>
                <p>• パワーメーターのあるアクティビティから自動的にゾーン分析を行います</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PowerZoneDashboard;